import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { EmptyState } from './EmptyState';
import { Loading } from './Loading';
import { ErrorAlert } from './ErrorAlert';
import { apiFetch } from '@/lib/api';
import { pnlColorClass } from '@/lib/colors';

interface ProfitData {
  date: string;
  profit: number;
}

const ProfitChart: React.FC = () => {
  const [data, setData] = useState<ProfitData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = () => {
      apiFetch('/api/profit')
        .then((r) => r.json())
        .then((d) => {
          setData(d);
          setLoading(false);
        })
        .catch(() => {
          setError('无法加载数据');
          setLoading(false);
        });
    };
    load();
    const timer = setInterval(load, 10000); // auto-refresh every 10s
    return () => clearInterval(timer);
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <Loading />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <ErrorAlert>{error}</ErrorAlert>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>收益曲线</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState title="暂无收益数据" description="完成配对交易后这里会显示累计盈亏曲线" />
        </CardContent>
      </Card>
    );
  }

  const totalProfit = data[data.length - 1].profit;
  const isPositive = totalProfit >= 0;
  const lineColor = isPositive ? '#15be53' : '#ea2261';

  return (
    <Card>
      <CardHeader>
        <CardTitle>收益曲线</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5edf5" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: '#64748d' }}
                axisLine={{ stroke: '#e5edf5' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#64748d' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                formatter={(value: number) => [`¥${value.toFixed(2)}`, '盈亏']}
                labelFormatter={(label) => `日期: ${label}`}
                contentStyle={{
                  borderRadius: '6px',
                  border: '1px solid #e5edf5',
                  boxShadow: 'rgba(50,50,93,0.25) 0px 6px 12px -2px, rgba(0,0,0,0.1) 0px 3px 7px -3px',
                }}
              />
              <Line
                type="monotone"
                dataKey="profit"
                stroke={lineColor}
                strokeWidth={2}
                dot={{ fill: lineColor, r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6, fill: lineColor, strokeWidth: 0 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="flex justify-around mt-6 pt-4 border-t border-[#e5edf5]">
          <div className="text-center">
            <p className="text-xs text-[#64748d] mb-1">累计盈亏</p>
            <p className={`text-xl font-[300] tracking-[-0.02em] tabular-nums ${pnlColorClass(isPositive)}`}>
              {isPositive ? '+' : ''}¥{totalProfit.toFixed(2)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-[#64748d] mb-1">交易日数</p>
            <p className="text-xl font-[300] tracking-[-0.02em] tabular-nums text-[#061b31]">
              {data.length}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfitChart;
